import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { format, addMonths, parseISO, set } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { CalendarContainer } from 'react-datepicker';
import Async from 'react-select/async';
import '../SearchPage/Calendar/ReactDatepicker.css';
import { components } from 'react-select';
import Select from 'react-select';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { ReactComponent as InfoIcon } from '../../assets/info.svg';
import { ReactComponent as NarrativeIcon } from '../NarrativeAddPage/assets/narrative.svg';
import { ReactComponent as ManualIcon } from '../NarrativeAddPage/assets/manual.svg';
import { ReactComponent as AnchorIcon } from '../../assets/anchor.svg';
import { ReactComponent as CloseIcon } from '../SearchPage/assets/close.svg';
import { ReactComponent as ImpactIcon } from '../NarrativeAddPage/assets/impact.svg';
import {ReactComponent as AIIcon} from '../../assets/ai.svg';
import {ReactComponent as PostIcon} from '../../assets/post.svg';
import { ReactComponent as ActorIcon } from '../../assets/actors.svg';
import { ReactComponent as SentimentAnalysisIcon } from '../../assets/sentimentAnalysis.svg';
import { ReactComponent as StoriesIcon } from '../../assets/buckets.svg';
import FacebookIcon from '../../assets/facebook.svg';
import TelegramIcon from '../../assets/telegram.svg';
import VkontakteIcon from '../../assets/vkontakte.svg';
import TwitterIcon from '../../assets/twitter.svg';
import WebIcon from '../../assets/web.svg';
import OkIcon from '../../assets/odnoklassniki.svg';
import YoutubeIcon from '../../assets/youtube.svg';
import TikTokIcon from '../../assets/tiktok.svg';

import * as d3 from 'd3';
import { mapThreatsStatus } from '../../utils/threats';
import { API } from '../../API';
import { ampli } from "../../ampli";
import { useCurrentUser } from '../../contexts/CurrentUser';
import { QueryInput } from '../NarrativeAddPage/QueryInput/QueryInput';
import { NearMixedPairWarning } from '../../components/NearMixedPairWarning/NearMixedPairWarning';
import Switch from '../../components/Switch';
import { languageOptions, languageNamesMapping, keywordsNativeLanguageOptions } from '../../utils/languages';
import { sentimentNameMapping } from '../../utils/sentiment';
import { LoaderSmall } from '../../components/LoaderSmall/LoaderSmall';
import { RangeSlider } from '../../components/RangeSlider/RangeSlider';
import styles from './NarrativeEditPage.module.scss';
import { ExcludeSwitch } from '../SearchPage/Filter/ExcludeSwitch';
import { QuotaCheckModal } from '../../components/QuotaCheckModal/QuotaCheckModal';
import { QuotaCheckModalOld } from '../../components/QuotaCheckModalOld/QuotaCheckModalOld';
import { mapIoCTypeOptions, IoCTypeOptionsList } from '../../utils/actors';
import { PlatformsFilter } from '../../components/FormFilters/Platforms/PlatformsFilter';
import { ContentTypesFilter } from '../../components/FormFilters/ContentTypes/ContentTypesFilter';
import { GeoGroupFilter } from '../SearchPage/Filter/GeoGroupFilter';
import DatePickerWrapper from '../../components/Datepicker/DatepickerWrapper';
import { NarrativeTypeChooser } from '../../components/FormFilters/NarrativeTypeChooser/NarrativeTypeChooser';

const formatNumberSignificant = d3.format('.3~s',);

const platformIconsMapping = {
  TELEGRAM: TelegramIcon,
  VK: VkontakteIcon,
  TWITTER: TwitterIcon,
  WEB: WebIcon,
  FACEBOOK: FacebookIcon,
  ADMIXER: WebIcon,
  OK: OkIcon,
  YOUTUBE: YoutubeIcon,
  TIKTOK: TikTokIcon,
}


export default function NarrativeEditPage({ isThreat = false }) {
  const { t } = useTranslation();
  let { narrativeId } = useParams();
  const [currentUser] = useCurrentUser();
  const [currentQuota] = useCurrentQuota();
  const notEnoughQuota =
    currentQuota?.billing_mode && currentQuota?.quota?.ANALYTICAL?.balance <= 0;
  const workspaceId = parseInt(localStorage.getItem('workspace_id'));

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const isCaseModerationEnabled = featureFlags?.includes('case_moderation');
  const showStoriesParameters = featureFlags?.includes(
    'show_stories_parameters',
  );
  const isAdvancedSyntax =
    workspaceConfig?.keywords_parser_class === 'Workspace16ESKeywordsQuery';
  const availableCaseTypes = workspaceConfig?.available_case_types || [];
  const aspectBasedSentimentAvailable = featureFlags?.includes(
    'aspect_based_sentiment',
  );
  const showFeedsFilter = featureFlags?.includes('show_feeds_filter');
  const showExamplesSearch = featureFlags?.includes('show_examples_search');
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');
  const limitTMUsage = featureFlags?.includes('limit_tm_usage');
  const showCountryGroupsFilter = featureFlags?.includes(
    'country_groups_filter',
  );
  const collectCommentsToPostsFlag = featureFlags?.includes(
    'collect_comments_to_posts',
  );
  const showStories = featureFlags?.includes('show_stories');

  const botsOptions = [
    { value: null, label: t('Include inauthentic behavior') },
    { value: false, label: t('Exclude inauthentic behavior') },
    { value: true, label: t('Show only inauthentic behavior') },
  ];

  const aspectBasedSentimentOptions = [
    {
      label: t('Sentiment towards a topic or narrative'),
      value: 'SENTIMENT_STATEMENT',
    },
    { label: t('Sentiment towards person'), value: 'SENTIMENT_PERSON' },
    {
      label: t('Sentiment towards organization'),
      value: 'SENTIMENT_ORGANIZATION',
    },
    { label: t('Sentiment towards country'), value: 'SENTIMENT_COUNTRY' },
    { label: t('Custom sentiment option'), value: 'SENTIMENT_CUSTOM' },
  ];

  let topicModelingTranslationLanguageOptions = [
    { label: t('English'), value: 'en' },
    { label: t('Ukrainian'), value: 'uk' },
    { label: t('French'), value: 'fr' },
    { label: t('Polish'), value: 'pl' },
    { label: t('Romanian'), value: 'ro' },
    { label: t('German'), value: 'de' },
    { label: t('Portuguese'), value: 'pt' },
  ];

  const narrativeTypes = [
    {
      type: 'NARRATIVE',
      title: t('Narrative'),
      icon: <NarrativeIcon />,
      description: t(
        'A case that automatically collects all data for a certain topic based on your search query. Can contain both boolean and AI search. Is updated every 30 minutes.',
      ),
    },
    {
      type: 'MANUAL',
      title: t('Manual'),
      icon: <ManualIcon />,
      description: t(
        'An empty case to which you can manually add publications from search and other cases. Changing the type to “Manual” case type will reset your search settings.',
      ),
    },
    {
      type: 'TOPIC_MODELING',
      title: t('Topic modeling'),
      icon: <ImpactIcon />,
      description: t(
        'A case to analyze public reaction to a particular media product.',
      ),
    },
  ];

  const sortingMenu = [
    {
      label: t('Rating'),
      value: 'rating',
    },
    {
      label: t('Recency'),
      value: 'recency',
    },
    {
      label: t('Event first seen'),
      value: 'first_message_date',
    },
    {
      label: t('Event last update'),
      value: 'last_message_date',
    },
    {
      label: t('Publications count'),
      value: 'messages_count',
    },
  ];

  const sortingOrder = [
    {
      label: t('Ascending order (A-Z)'),
      value: true,
    },
    {
      label: t('Descending order (Z-A)'),
      value: false,
    },
  ];

  const [currentNarrativeType, setCurrentNarrativeType] = useState(null);

  const showTopicsSection =
    (narrativeTypes[currentNarrativeType]?.type !== 'TOPIC_MODELING' &&
      showStories) ||
    narrativeTypes[currentNarrativeType]?.type === 'TOPIC_MODELING';
  const showAIAnalysisSection =
    aspectBasedSentimentAvailable || showTopicsSection;

  let navigate = useNavigate();

  const [narrative, setNarrative] = useState(null);
  const [values, setValues] = useState({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState({});

  const [isQuotaChecked, setIsQuotaChecked] = useState(false);
  const [caseCreation, setCaseCreation] = useState(false);
  const [isCaseOpen, setIsCaseOpen] = useState(false);
  const [quotaModal, setQuotaModal] = useState({ isOpen: false, data: null });
  const [sourceTypes, setSourceTypes] = useState([]);
  const [sourceGroups, setSourceGroups] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [geoGroup, setGeoGroup] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [isManuallyManaged, setIsManuallyManaged] = useState(false);
  const [applySupervisedClassifier, setApplySupervisedClassifier] =
    useState(false);
  const [disableFilters, setDisableFilters] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [refreshHistory, setrefreshHistory] = useState(true);
  const [examples, setExamples] = useState(['']);
  const [languages, setLanguages] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countriesTop, setCountriesTop] = useState([]);
  const [showBotsOnly, setShowBotsOnly] = useState(null);
  const [discreditations, setDiscreditations] = useState([]);
  const [affiliationAll, setAffliationAll] = useState(false);
  const [affiliationCountries, setAffiliationCountries] = useState([]);
  const [similarity_threshold, setSimilarityThreshold] = useState(null);
  const [defaultSimilarityThreshold, setDefaultSimilarityThreshold] =
    useState(0.5);
  const [isMultilang, setIsMultilang] = useState(false);
  const [nativeLang, setNativeLang] = useState(null);
  const [sentiment, setSentiment] = useState([]);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [isValidQuery, setIsValidQuery] = useState(true);
  const [hasMixedNearPair, setHasMixedNearPair] = useState(false);
  const [keywords, setKeywords] = useState(null);
  const keywordsRef = useRef(null);
  const queryInputRef = useRef(null);
  const [createStories, setCreateStories] = useState(false);
  const [storiesCandidatesMinThreshold, setStoriesCandidatesMinThreshold] =
    useState(0.8);
  const [storiesGroupingMaxDistance, setStoriesGroupingMaxDistance] =
    useState(0.64);
  const [useStoriesGroupingMaxDistance, setUseStoriesGroupingMaxDistance] =
    useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isAspectBasedSentimentEnabled, setIsAspectBasedSentimentEnabled] =
    useState(false);
  const [aspectBasedSentimentObject, setAspectBasedSentimentObject] =
    useState(null);
  const [aspectBasedSentimentSubject, setAspectBasedSentimentSubject] =
    useState('');
  const [collectCommentsToPosts, setCollectCommentsToPosts] = useState(false);
  const [topicModelingEntity, setTopicModelingEntity] = useState(null);
  const [isParametersDisabled, setIsParametersDisabled] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [addDateRangeSettings, setAddDateRangeSettings] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState(null);
  const [startDatePreset, setStartDatePreset] = useState(null);
  const [endDatePreset, setEndDatePreset] = useState(null);
  const [defaultSorting, setDefaultSorting] = useState({
    fieldName: 'rating',
    isAscending: false,
  });

  const [
    topicModelingTranslationLanguage,
    setTopicModelingTranslationLanguage,
  ] = useState(null);
  const isValidQueryRef = useRef(isValidQuery);

  const [addDefaultSorting, setAddDefaultSorting] = useState(false);

  const sortingQuery = defaultSorting.fieldName
    ? `${defaultSorting.isAscending ? '' : '-'}${defaultSorting.fieldName}`
    : null;
  const [languagesExclude, setLanguagesExclude] = useState(false);
  const [countriesExclude, setCountriesExclude] = useState(false);
  const [sourceGroupsExclude, setSourceGroupsExclude] = useState(false);
  const [feedsExclude, setFeedsExclude] = useState(false);
  const [sourcesExclude, setSourcesExclude] = useState(false);
  const [affiliationCountriesExclude, setAffiliationCountriesExclude] =
    useState(false);
  const [geoGroupExclude, setGeoGroupExclude] = useState(false);

  const [storiesVectorsModel, setStoriesVectorsModel] = useState(null);
  const [storiesVectorsModelOptions, setStoriesVectorsModelOptions] = useState(
    [],
  );
  const [includeCommentsToStories, setIncludeCommentsToStories] =
    useState(false);

  const [widgetsSettings, setWidgetsSettings] = useState(false);
  const [summaryPanel, setSummaryPanel] = useState(null);
  const [eventCard, setEventCard] = useState(null);
  const [opinionCard, setOpinionCard] = useState(null);
  const [sentimentBar, setSentimentBar] = useState(null);
  const [geoWidget, setGeoWidget] = useState(true);
  const [topActors, setTopActors] = useState(null);
  const [actorTypes, setActorTypes] = useState(true);
  const [evolutionChart, setEvolutionChart] = useState(true);

  const [evolutionTabs, setEvolutionTabs] = useState([]);
  const [evolutionBreakdowns, setEvolutionBreakdowns] = useState(null);

  const evolutionTabsOptions = [
    { value: 'publications', label: t('Publications') },
    { value: 'impressions', label: t('Views') },
    { value: 'engagement', label: t('Engagement') },
  ];

  const evolutionBreakdownsOptions = [
    { value: 'aggregation', label: t('Stats aggregation') },
    { value: 'breakdown', label: t('Stats breakdown') },
  ];

  const summaryPanelOptions = [
    { value: 'standard', label: t('Standard') },
    { value: 'custom', label: t('Custom') },
  ];

  const eventCardOptions = [
    { value: 'rating', label: t('Rating') },
    { value: 'messages', label: t('Publications') },
  ];

  const opinionCardOptions = [
    { value: 'rating', label: t('Rating') },
    { value: 'messages', label: t('Publications') },
  ];

  const sentimentBarOptions = [
    { value: 'messages', label: t('Publications') },
    { value: 'posts', label: t('Posts') },
  ];

  const topActorsOptions = [
    { value: 'audience', label: t('Sort by audience') },
    { value: 'messages_count', label: t('Sort by number of publications') },
  ];

  const getActiveExclusiveFilter = () => {
    if (sourceGroups?.length > 0) return 'Actor groups';
    if (affiliationCountries?.length > 0) return 'State-affiliated';
    if (discreditations?.length > 0) return 'High risk accounts';
    if (showBotsOnly === true) return 'Show only inauthentic behavior'; // Only when showing ONLY bots
    return null;
  };

  const activeExclusiveFilter = getActiveExclusiveFilter();

  const isFilterDisabled = (filterName) => {
    return activeExclusiveFilter && activeExclusiveFilter !== filterName;
  };

  const getDisabledTooltip = (filterName) => {
    if (!isFilterDisabled(filterName)) return null;
    return t(`This filter can't be combined with '{{activeFilter}}'`, {
      activeFilter: t(activeExclusiveFilter),
    });
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setIsLoading(false);
    }
  }, [errors]);

  useEffect(() => {
    API.fetch('GET', '/API/v1/config/website_config').then((websiteConfig) => {
      if (
        !narrative?.parameters?.stories_candidates_min_threshold &&
        websiteConfig?.stories_candidates_min_threshold
      ) {
        setStoriesCandidatesMinThreshold(
          parseFloat(websiteConfig.stories_candidates_min_threshold),
        );
      }

      if (
        !narrative?.parameters?.stories_grouping_max_distance &&
        websiteConfig?.stories_grouping_max_distance
      ) {
        setStoriesGroupingMaxDistance(
          parseFloat(websiteConfig.stories_grouping_max_distance),
        );
      }

      if (workspaceConfig?.narratives_similarity_threshold) {
        setDefaultSimilarityThreshold(
          parseFloat(workspaceConfig?.narratives_similarity_threshold),
        );
      } else if (websiteConfig?.narratives_similarity_threshold) {
        setDefaultSimilarityThreshold(
          parseFloat(websiteConfig.narratives_similarity_threshold),
        );
      }
    });
  }, [narrative]);

  const getWorkspaces = (inputValue, callback) => {
    return API.fetch('GET', `/API/v1/workspaces?q=${inputValue}`).then(
      (data) => {
        callback(
          data.objects
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((workspace) => {
              return { value: workspace.id, label: workspace.name };
            }),
        );
      },
    );
  };

  const getStoriesVectorsModelOptions = useCallback(() => {
    API.fetch('GET', `/API/v1/config/vectorization_models`).then((data) => {
      let default_value = { value: null, label: 'Default' };

      setStoriesVectorsModelOptions([
        default_value,
        ...Object.entries(data.models).map(([name, code]) => {
          return { value: code, label: name };
        }),
      ]);
    });
  }, []);

  useEffect(getStoriesVectorsModelOptions, [getStoriesVectorsModelOptions]);

  let workspacesPromiseTimeout;
  const workspacePromiseOptions = function (inputValue) {
    if (workspacesPromiseTimeout !== null) {
      clearTimeout(workspacesPromiseTimeout);
    }

    if (!inputValue) {
      return [];
    }

    return new Promise((resolve) => {
      workspacesPromiseTimeout = setTimeout(() => {
        getWorkspaces(inputValue, resolve);
      }, 1000);
    });
  };

  const getWorkspacesList = () => {
    if (!currentUser?.is_super_admin) {
      return;
    }

    if (!narrative?.workspace_ids || narrative?.workspace_ids?.length === 0) {
      return;
    }

    const urlParams = new URLSearchParams();

    narrative?.workspace_ids?.forEach((id) => urlParams.append('ids', id));

    API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
      (data) => {
        setWorkspaces(
          data.objects.map((w) => {
            return { value: w.id, label: w.name };
          }),
        );
      },
    );
  };
  useEffect(getWorkspacesList, [narrative]);

  const reviewStatusOptions = [
    {
      value: 'PENDING',
      label: t('Pending'),
    },
    {
      value: 'ACCEPTED',
      label: t('Accepted'),
    },
    {
      value: 'DECLINED',
      label: t('Declined'),
    },
  ];

  useEffect(() => {
    if (
      examples.filter((e) => e.length > 0).length === 0 &&
      similarity_threshold !== null
    ) {
      setSimilarityThreshold(null);
    }
    if (
      examples.filter((e) => e.length > 0).length > 0 &&
      similarity_threshold === null
    ) {
      setSimilarityThreshold(defaultSimilarityThreshold);
    }
  }, [examples]);

  useEffect(() => {
    if (
      currentNarrativeType &&
      ['MANUAL', 'TOPIC_MODELING'].includes(
        narrativeTypes[currentNarrativeType].type,
      )
    ) {
      setrefreshHistory(false);
    } else {
      setrefreshHistory(true);
    }
  }, [currentNarrativeType]);

  const sentimentOptions = [
    { value: 'POSITIVE', label: t('Positive') },
    { value: 'NEUTRAL', label: t('Neutral') },
    { value: 'NEGATIVE', label: t('Negative') },
  ];

  useEffect(() => {
    if (!isMultilang) {
      setNativeLang(null);
    }
  }, [isMultilang]);

  const onKeywordsChange = useCallback((value, isValid) => {
    keywordsRef.current = value;
    setKeywords(value);
    if (isValid !== null) {
      setIsValidQuery(isValid);
    } else if (isValid === null && !isValidQueryRef.current) {
      setIsValidQuery(true);
    }
  }, []);

  const deleteExample = (e, index) => {
    e.preventDefault();
    setExamples((old) => {
      return [...old.filter((v, i) => i !== index)];
    });
  };
  const handleChange = (e) => {
    setValues((oldValues) => ({
      ...oldValues,
      [e.target.name]: e.target.value,
    }));
  };

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      API.fetch('GET', `/API/v1/groups?q=${inputValue}&sorting=-id`).then(
        (data) => {
          resolve(
            data.objects.map((group) => {
              return { value: group.id, label: group.name };
            }),
          );
        },
      );
    });

  const promiseFeedOptions = (inputValue) =>
    new Promise((resolve) => {
      API.fetch('GET', `/API/v1/feeds?q=${inputValue}`).then((data) => {
        resolve(
          data.objects
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((feed) => {
              return { value: feed.id, label: feed.name };
            }),
        );
      });
    });

  const getSources = useMemo(
    () =>
      debounce((inputValue, callback) => {
        API.fetch(
          'GET',
          `/API/v1/sources?q=${inputValue}&sorting=-audience`,
        ).then((data) => {
          callback(
            data.objects.map((source) => {
              let followers_cnt = source.audience;
              return {
                value: source.id,
                label: source.name,
                icon: platformIconsMapping[source.source_type] || WebIcon,
                followers_count: followers_cnt || 0,
              };
            }),
          );
        });
      }, 1000),
    [],
  );

  const { Option } = components;
  const IconOption = (props) => {
    return (
      <Option {...props}>
        <img
          src={props.data.icon}
          style={{ width: 15, position: 'relative', top: 2, marginRight: 2 }}
          alt={props.data.label}
        />{' '}
        {props.data.label}{' '}
        <b>({formatNumberSignificant(props.data.followers_count)})</b>
      </Option>
    );
  };

  const getAllCountries = useCallback(() => {
    API.fetch('GET', `/API/v1/countries?page=1&size=100`).then((data) => {
      setCountriesTop(
        data.objects.slice(0, 10).map((country) => {
          return { value: country.id, label: country.name };
        }),
      );
    });
  }, []);

  useEffect(getAllCountries, [getAllCountries]);

  const getCountries = (inputValue, callback) => {
    return API.fetch('GET', `/API/v1/countries?q=${inputValue}`).then(
      (data) => {
        callback(
          data.objects
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((country) => {
              return { value: country.id, label: country.name };
            }),
        );
      },
    );
  };

  const sourcesPromiseOptions = (inputValue) => {
    if (!inputValue) {
      return [];
    }
    return new Promise((resolve) => {
      getSources(inputValue, resolve);
    });
  };

  let countriesPromiseTimeout;
  const countriesPromiseOptions = function (inputValue) {
    if (countriesPromiseTimeout !== null) {
      clearTimeout(countriesPromiseTimeout);
    }

    if (!inputValue) {
      return [];
    }

    return new Promise((resolve) => {
      countriesPromiseTimeout = setTimeout(() => {
        getCountries(inputValue, resolve);
      }, 1000);
    });
  };

  const handleEdit = () => {
    setErrors({});
    setIsLoading(true);

    if (isQuotaChecked) {
      setCaseCreation(true);
    }
    const headers = { 'Content-Type': 'application/json' };

    if (isThreat && narrative?.workspace_id !== currentUser?.workspace?.id) {
      headers['workspace-id'] = narrative?.workspace_id;
    }

    let parameters = {};
    let type = narrativeTypes[currentNarrativeType].type;

    if (narrativeTypes[currentNarrativeType].type === 'MANUAL') {
      parameters = null;
    }

    let excludingFilters = [];
    if (languagesExclude) {
      excludingFilters.push('languages');
    }

    if (countriesExclude) {
      excludingFilters.push('source_origin_country_ids');
    }

    if (sourceGroupsExclude) {
      excludingFilters.push('source_group_ids');
    }

    if (feedsExclude) {
      excludingFilters.push('feed_ids');
    }

    if (geoGroupExclude) {
      excludingFilters.push('country_group_ids');
    }

    if (sourcesExclude) {
      excludingFilters.push('source_ids');
    }

    if (affiliationCountriesExclude) {
      if (affiliationAll) {
        excludingFilters.push('source_state_affiliated_all_countries');
      } else {
        excludingFilters.push('source_state_affiliated_country_ids');
      }
    }

    if (showBotsOnly === false) {
      excludingFilters.push('source_bots');
    }

    if (narrativeTypes[currentNarrativeType].type === 'TOPIC_MODELING') {
      parameters = {
        start_date: startDate ? format(startDate, 'yyyy-LL-dd') : null,
        end_date: endDate && !isCaseOpen ? format(endDate, 'yyyy-LL-dd') : null,
        keywords: disableFilters ? [] : keywords ? [keywords] : [],
        topic_modeling_dashboard_dates_preset:
          addDateRangeSettings && dateRangePreset && topicModelingEntity.value !== 'OPINIONS_ONLY'
            ? dateRangePreset.value
            : null,
        topic_modeling_dashboard_start_date:
          addDateRangeSettings && startDatePreset &&
          dateRangePreset.value === 'FIXED' &&
          topicModelingEntity.value !== 'OPINIONS_ONLY'
            ? format(startDatePreset, 'yyyy-LL-dd')
            : null,
        topic_modeling_dashboard_end_date:
          addDateRangeSettings && endDatePreset &&
          dateRangePreset.value === 'FIXED' &&
          topicModelingEntity.value !== 'OPINIONS_ONLY'
            ? format(endDatePreset, 'yyyy-LL-dd')
            : null,
        topic_modeling_dashboard_sort: addDefaultSorting ? sortingQuery : null,
      };
    }

    if (narrativeTypes[currentNarrativeType].type === 'NARRATIVE') {
      if (disableFilters && sourceGroups.length < 1) {
        setErrors((oldValues) => ({
          ...oldValues,
          sourceGroups: t('At least one source must be selected'),
        }));
        return;
      }

      if (isMultilang && nativeLang === null) {
        setErrors((oldValues) => ({
          ...oldValues,
          language: t(
            'You must select a native language for multilanguage search',
          ),
        }));
        return;
      }

      if (
        examples.filter((e) => e.length > 0).length === 0 &&
        similarity_threshold
      ) {
        setErrors((oldValues) => ({
          ...oldValues,
          similarity_threshold: t(
            "You can't set similarity threshold and do not provide any examples",
          ),
        }));
        return;
      }

      parameters = {
        keywords: disableFilters ? [] : keywords ? [keywords] : [],
        examples: disableFilters ? [] : examples.filter((e) => e.length > 0),
        start_date: startDate ? format(startDate, 'yyyy-LL-dd') : null,
        end_date: endDate && !isCaseOpen ? format(endDate, 'yyyy-LL-dd') : null,
        apply_supervised_classifier: applySupervisedClassifier,
        source_types: sourceTypes,
        source_group_ids: sourceGroups.map((v) => v.value),
        disable_filters: disableFilters,
        languages: disableFilters ? [] : languages.map((v) => v.value),
        source_ids: disableFilters ? [] : sources.map((v) => v.value),
        source_origin_country_ids: disableFilters
          ? []
          : countries.map((v) => v.value),
        translate_keywords_query: isMultilang,
        keywords_query_origin_language: nativeLang ? nativeLang.value : '',
        sentiment: sentiment.map((score) => score.value),
        content_types: disableFilters ? [] : contentTypes,
        similarity_threshold: similarity_threshold
          ? similarity_threshold
          : null,
        create_stories: createStories,
        stories_candidates_min_threshold: createStories
          ? storiesCandidatesMinThreshold
          : null,
        stories_grouping_max_distance:
          createStories && useStoriesGroupingMaxDistance
            ? storiesGroupingMaxDistance
            : null,
        source_state_affiliated_all_countries: affiliationAll,
        source_state_affiliated_country_ids: affiliationAll
          ? []
          : affiliationCountries.map((v) => v.value),
        feed_ids: feeds.map((v) => v.value),
        source_discreditated_entity_types: discreditations.map((v) => v.value),
        source_bots: showBotsOnly !== null ? showBotsOnly : null,
        aspect_based_sentiment: isAspectBasedSentimentEnabled,
        aspect_based_sentiment_object: isAspectBasedSentimentEnabled
          ? aspectBasedSentimentObject.value
          : null,
        aspect_based_sentiment_subject: isAspectBasedSentimentEnabled
          ? aspectBasedSentimentSubject
          : null,
        collect_comments_to_posts:
          collectCommentsToPosts && contentTypes.includes('POST'),
        exclusive_filters:
          excludingFilters.length > 0 ? excludingFilters : null,
        stories_vectors_model: storiesVectorsModel
          ? storiesVectorsModel.value
          : null,
        include_comments_to_stories: includeCommentsToStories,
        country_group_ids: geoGroup?.length > 0 ? geoGroup : [],
      };
    }

    let widgetsConfig = null;

    if (widgetsSettings) {
      widgetsConfig = {
        summary_panel: summaryPanel?.value || null,
        event_card: eventCard?.value || null,
        opinion_card: opinionCard?.value || null,
        sentiment_bar:
          sentimentBar?.length > 0 ? sentimentBar.map((v) => v.value) : null,
        geo_widget: geoWidget,
        top_actors: topActors?.value || null,
        actor_types: actorTypes,
        evolution_tabs:
          evolutionTabs?.length > 0 ? evolutionTabs.map((v) => v.value) : null,
        evolution_breakdowns:
          evolutionBreakdowns?.length > 0
            ? evolutionBreakdowns.map((v) => v.value)
            : null,
        evolution_chart: evolutionChart,
      };
    }

    API.fetch('PATCH', `/API/v1/narratives/${narrativeId}`, headers, {
      name: values.name,
      description: values.description,
      type: narrative?.type === 'INCIDENT' ? 'INCIDENT' : type === 'MANUAL' ? 'NARRATIVE' : type,
      is_active: isActive,
      is_manual: narrativeTypes[currentNarrativeType].type === 'MANUAL',
      is_threat: isThreat,
      is_created_by_ucbi_reporter: narrative?.is_created_by_ucbi_reporter,
      incident_type: narrative?.incident_type || null,
      topic: narrative?.topic || null,
      geo_scope: narrative?.geo_scope || null,
      workspace_ids:
        workspaces?.map((v) => v.value) || narrative?.workspace_ids || null,
      review_status: reviewStatus.value,
      ...(['TOPIC_MODELING'].includes(type)
        ? {
            parameters: {
              ...narrative.parameters,
              ...parameters,
              topic_modeling_widget_config: widgetsConfig,
            },
          }
        : { parameters: parameters }),
      confirm_quota_usage: isQuotaChecked,
      is_manually_managed: isManuallyManaged,
    })
      .then((data) => {
        ampli.track({
          event_type: 'Edit case',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            narrative_id: narrativeId,
            is_stories_applied: createStories,
            is_aspect_based_sentiment_applied: isAspectBasedSentimentEnabled,
          },
        });

        if (!isQuotaChecked) {
          setIsLoading(false);
          setIsQuotaChecked(true);

          if (data?.id) {
            setIsLoading(false);
            if (isThreat) {
              navigate(`/threats/${data.id}`);
            } else {
              navigate(`/narratives/${data.id}`);
            }
          } else {
            setQuotaModal({ isOpen: true, data: data });
          }
        } else {
          setIsLoading(false);
          setCaseCreation(false);

          if (isThreat) {
            navigate(`/threats/${data.id}`);
          } else {
            navigate(`/narratives/${data.id}`);
          }
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setErrors({ ...errors, general: e.message });
      });
  };

  const handleCopyFromEdit = () => {
    setErrors({});
    setIsLoading(true);

    if (isQuotaChecked) {
      setCaseCreation(true);
    }

    const headers = { 'Content-Type': 'application/json' };

    if (isThreat && narrative?.workspace_id !== currentUser?.workspace?.id) {
      headers['workspace-id'] = narrative?.workspace_id;
    }

    let parameters = null;
    let type = narrativeTypes[currentNarrativeType].type;
    let excludingFilters = [];
    if (languagesExclude) {
      excludingFilters.push('languages');
    }

    if (countriesExclude) {
      excludingFilters.push('source_origin_country_ids');
    }

    if (sourceGroupsExclude) {
      excludingFilters.push('source_group_ids');
    }

    if (sourcesExclude) {
      excludingFilters.push('source_ids');
    }

    if (affiliationCountriesExclude) {
      excludingFilters.push('source_state_affiliated_country_ids');
    }

    if (feedsExclude) {
      excludingFilters.push('feed_ids');
    }

    if (geoGroupExclude) {
      excludingFilters.push('country_group_ids');
    }

    if (showBotsOnly === false) {
      excludingFilters.push('source_bots');
    }

    if (narrativeTypes[currentNarrativeType].type === 'NARRATIVE') {
      if (disableFilters && sourceGroups.length < 1) {
        setErrors((oldValues) => ({
          ...oldValues,
          sourceGroups: t('At least one source must be selected'),
        }));
        return;
      }

      if (isMultilang && nativeLang === null) {
        setErrors((oldValues) => ({
          ...oldValues,
          language: t(
            'You must select a native language for multilanguage search',
          ),
        }));
        return;
      }

      if (
        examples.filter((e) => e.length > 0).length === 0 &&
        similarity_threshold
      ) {
        setErrors((oldValues) => ({
          ...oldValues,
          similarity_threshold: t(
            "You can't set similarity threshold and do not provide any examples",
          ),
        }));
        return;
      }

      parameters = {
        keywords: keywords ? [keywords] : [],
        examples: examples.filter((e) => e.length > 0),
        start_date: startDate ? format(startDate, 'yyyy-LL-dd') : null,
        end_date: endDate && !isCaseOpen ? format(endDate, 'yyyy-LL-dd') : null,
        apply_supervised_classifier: applySupervisedClassifier,
        source_types: sourceTypes,
        source_group_ids: sourceGroups.map((v) => v.value),
        languages: disableFilters ? [] : languages.map((v) => v.value),
        source_ids: disableFilters ? [] : sources.map((v) => v.value),
        source_origin_country_ids: disableFilters
          ? []
          : countries.map((v) => v.value),
        translate_keywords_query: isMultilang,
        keywords_query_origin_language: nativeLang ? nativeLang.value : '',
        sentiment: sentiment.map((score) => score.value),
        content_types: disableFilters ? [] : contentTypes,
        similarity_threshold: similarity_threshold
          ? similarity_threshold
          : null,
        create_stories: createStories,
        stories_candidates_min_threshold: createStories
          ? storiesCandidatesMinThreshold
          : null,
        stories_grouping_max_distance:
          createStories && useStoriesGroupingMaxDistance
            ? storiesGroupingMaxDistance
            : null,
        source_state_affiliated_all_countries: affiliationAll,
        source_state_affiliated_country_ids: affiliationAll
          ? []
          : affiliationCountries.map((v) => v.value),
        feed_ids: feeds.map((v) => v.value),
        source_discreditated_entity_types: discreditations.map((v) => v.value),
        source_bots: showBotsOnly !== null ? showBotsOnly : null,
        aspect_based_sentiment: isAspectBasedSentimentEnabled,
        aspect_based_sentiment_object: isAspectBasedSentimentEnabled
          ? aspectBasedSentimentObject.value
          : null,
        aspect_based_sentiment_subject: isAspectBasedSentimentEnabled
          ? aspectBasedSentimentSubject
          : null,
        collect_comments_to_posts:
          collectCommentsToPosts && contentTypes.includes('POST'),
        exclusive_filters:
          excludingFilters.length > 0 ? excludingFilters : null,
        stories_vectors_model: storiesVectorsModel
          ? storiesVectorsModel.value
          : null,
        include_comments_to_stories: includeCommentsToStories,
        country_group_ids: geoGroup?.length > 0 ? geoGroup : [],
      };
    }

    API.fetch('POST', '/API/v1/narratives', headers, {
      name: values.name,
      description: values.description,
      type: narrative?.type === 'INCIDENT' ? 'INCIDENT' : type === 'MANUAL' ? 'NARRATIVE' : type,
      is_manual: narrativeTypes[currentNarrativeType].type === 'MANUAL',
      review_status: reviewStatus.value,
      is_active: isActive,
      is_threat: isThreat,
      is_created_by_ucbi_reporter: narrative?.is_created_by_ucbi_reporter,
      incident_type: narrative?.incident_type || null,
      topic: narrative?.topic || null,
      geo_scope: narrative?.geo_scope || null,
      workspace_ids: narrative?.workspace_ids || null,
      on_create: {
        from_narrative_id: narrativeId,
      },
      parameters: parameters,
      confirm_quota_usage: isQuotaChecked,
      is_manually_managed: isManuallyManaged,
    })
      .then((data) => {
        ampli.track({
          event_type: 'Edit case',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            narrative_id: data.id,
            is_stories_applied: createStories,
            is_aspect_based_sentiment_applied: isAspectBasedSentimentEnabled,
          },
        });

        if (!isQuotaChecked) {
          setIsLoading(false);
          setIsQuotaChecked(true);
          if (data?.id) {
            API.fetch('DELETE', `/API/v1/narratives/${narrativeId}`).then(
              () => {
                setIsLoading(false);
                if (isThreat) {
                  navigate(`/threats/${data.id}`);
                } else {
                  navigate(`/narratives/${data.id}`);
                }
              },
            );
          } else {
            setQuotaModal({ isOpen: true, data: data });
          }
        } else {
          API.fetch('DELETE', `/API/v1/narratives/${narrativeId}`).then(() => {
            setIsLoading(false);
            setCaseCreation(false);
            if (isThreat) {
              navigate(`/threats/${data.id}`);
            } else {
              navigate(`/narratives/${data.id}`);
            }
          });
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setErrors({ ...errors, general: e.message });
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (values.name.trim().length === 0) {
      setErrors((oldValues) => ({
        ...oldValues,
        name: t('You must provide a valid name'),
      }));
      return;
    }

    if (narrativeTypes[currentNarrativeType].type === 'NARRATIVE') {
      if (
        (keywords?.trim().length === 0 || keywords === null) &&
        sources?.length === 0 &&
        !disableFilters &&
        examples?.filter((e) => e.length > 0).length === 0 &&
        sourceGroups.length === 0
      ) {
        setErrors((oldValues) => ({
          ...oldValues,
          general: `To save the changes, you must provide at least one of the following: add keywords, add examples, choose at least one actor or an actor group.`,
        }));
        return;
      }
    }

    if (
      aspectBasedSentimentObject !== null &&
      aspectBasedSentimentSubject.trim().length === 0
    ) {
      setErrors((oldValues) => ({
        ...oldValues,
        aspectBasedSentimentSubject: t(
          'You must provide a subject for aspect based sentiment analysis',
        ),
      }));
      return;
    }

    if (!refreshHistory) {
      handleEdit();
    } else {
      handleCopyFromEdit();
    }
  };

  useEffect(() => {
    API.fetch('GET', `/API/v1/narratives/${narrativeId}`).then((data) => {
      const typeIndex = narrativeTypes.findIndex((type) => type.type === data.type);
      setCurrentNarrativeType(data.is_manual ? 1 : Math.max(typeIndex, 0));
      setValues({ name: data.name, description: data.description });
      setIsActive(data.is_active);
      setNarrative(data);
      setIsParametersDisabled(['TOPIC_MODELING'].includes(data.type));
    });
  }, [narrativeId]);

  useEffect(() => {
    if (narrative?.parameters?.keywords) {
      const keywordsValue = String(narrative?.parameters?.keywords);
      keywordsRef.current = keywordsValue;
      queryInputRef?.current?.set(keywordsValue);
      setKeywords(keywordsValue);
    }

    if (narrative?.parameters?.examples) {
      setExamples(narrative?.parameters?.examples);
    }

    if (narrative?.parameters?.languages) {
      setLanguages(
        narrative?.parameters?.languages.map((v) => ({
          value: v,
          label: languageNamesMapping[v],
        })),
      );
    }

    if (narrative?.parameters?.similarity_threshold) {
      setSimilarityThreshold(narrative?.parameters?.similarity_threshold);
    }

    if (narrative?.parameters?.content_types) {
      setContentTypes(narrative?.parameters?.content_types);
    }

    if (narrative?.parameters?.start_date) {
      setStartDate(parseISO(narrative?.parameters?.start_date));
    }

    if (narrative?.parameters?.end_date) {
      setIsCaseOpen(false);
      setEndDate(new Date(narrative?.parameters?.end_date));
    } else {
      setIsCaseOpen(true);
    }

    if (narrative?.parameters?.apply_supervised_classifier !== undefined) {
      setApplySupervisedClassifier(
        narrative?.parameters?.apply_supervised_classifier,
      );
    }
    if (narrative?.parameters?.source_types) {
      setSourceTypes(narrative?.parameters?.source_types);
    }

    if (narrative?.parameters?.source_group_ids) {
      const promises = narrative?.parameters?.source_group_ids.map(
        (source_group_id) =>
          API.fetch('GET', `/API/v1/groups/${source_group_id}`),
      );

      Promise.all(promises).then((results) =>
        setSourceGroups(
          results
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((v) => ({ value: v.id, label: v.name })),
        ),
      );
    }

    if (narrative?.parameters?.feed_ids) {
      const promises = narrative?.parameters?.feed_ids.map((feed_id) =>
        API.fetch('GET', `/API/v1/feeds?ids=${feed_id}`),
      );

      Promise.all(promises).then((results) =>
        setFeeds(
          results
            .sort((a, b) => (a.objects[0].name > b.objects[0].name ? 1 : -1))
            .map((v) => ({ value: v.objects[0].id, label: v.objects[0].name })),
        ),
      );
    }

    if (narrative?.parameters?.country_group_ids) {
      setGeoGroup(narrative?.parameters?.country_group_ids);
    }

    if (narrative?.parameters?.source_ids) {
      const promises = narrative?.parameters?.source_ids.map((source_id) =>
        API.fetch('GET', `/API/v1/sources/${source_id}`),
      );

      Promise.all(promises).then((results) =>
        setSources(
          results
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((v) => ({ value: v.id, label: v.name })),
        ),
      );
    }

    if (narrative?.parameters?.source_origin_country_ids) {
      const promises = narrative?.parameters?.source_origin_country_ids.map(
        (country_id) => API.fetch('GET', `/API/v1/countries/${country_id}`),
      );

      Promise.all(promises).then((results) =>
        setCountries(
          results
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((v) => ({ value: v.id, label: v.name })),
        ),
      );
    }

    if (narrative?.parameters?.disable_filters !== undefined) {
      setDisableFilters(narrative?.parameters?.disable_filters);
    }

    if (narrative?.parameters?.translate_keywords_query === true) {
      setIsMultilang(true);
      setNativeLang({
        value: narrative?.parameters?.keywords_query_origin_language,
        label:
          languageNamesMapping[
            narrative?.parameters?.keywords_query_origin_language
          ],
      });
    }

    if (narrative?.review_status) {
      setReviewStatus({
        value: narrative.review_status,
        label: t(mapThreatsStatus[narrative.review_status]),
      });
    }

    if (
      narrative?.parameters?.sentiment !== null &&
      narrative?.parameters?.sentiment.length > 0
    ) {
      const sentimentList = narrative?.parameters?.sentiment.map(
        (sentiment) => {
          return { value: sentiment, label: sentimentNameMapping[sentiment] };
        },
      );
      setSentiment(sentimentList);
    }

    setCreateStories(narrative?.parameters?.create_stories);
    setCollectCommentsToPosts(narrative?.parameters?.collect_comments_to_posts);

    if (narrative?.parameters?.stories_candidates_min_threshold) {
      setStoriesCandidatesMinThreshold(
        narrative?.parameters?.stories_candidates_min_threshold,
      );
    }

    if (narrative?.parameters?.stories_grouping_max_distance) {
      setStoriesGroupingMaxDistance(
        narrative?.parameters?.stories_grouping_max_distance,
      );
      setUseStoriesGroupingMaxDistance(true);
    }

    if (narrative?.parameters?.source_bots === true) {
      setShowBotsOnly(true);
    }

    if (narrative?.parameters?.source_state_affiliated_all_countries === true) {
      setAffliationAll(true);
    }

    if (narrative?.parameters?.source_discreditated_entity_types) {
      const IoCTypes = mapIoCTypeOptions(t);
      let selected =
        narrative?.parameters?.source_discreditated_entity_types.map(
          (option) => IoCTypes[option],
        );
      setDiscreditations(selected);
    }

    if (narrative?.parameters?.source_state_affiliated_country_ids) {
      const promises =
        narrative?.parameters?.source_state_affiliated_country_ids.map(
          (country_id) => API.fetch('GET', `/API/v1/countries/${country_id}`),
        );

      Promise.all(promises).then((results) =>
        setAffiliationCountries(
          results
            .sort((a, b) => (a.name > b.name ? 1 : -1))
            .map((v) => ({ value: v.id, label: v.name })),
        ),
      );
    }

    if (narrative?.parameters?.aspect_based_sentiment) {
      setIsAspectBasedSentimentEnabled(
        narrative?.parameters?.aspect_based_sentiment,
      );
    }

    if (narrative?.parameters?.aspect_based_sentiment_object) {
      let selected = aspectBasedSentimentOptions.filter(
        (option) =>
          option.value === narrative?.parameters?.aspect_based_sentiment_object,
      );

      setAspectBasedSentimentObject(selected[0]);
    }

    if (narrative?.parameters?.aspect_based_sentiment_subject) {
      setAspectBasedSentimentSubject(
        narrative?.parameters?.aspect_based_sentiment_subject,
      );
    }

    if (narrative?.parameters?.topic_modeling_entity) {
      let selected = topicModelingEntityOptions.filter(
        (option) =>
          option.value === narrative?.parameters?.topic_modeling_entity,
      );

      setTopicModelingEntity(selected[0]);
    }

    if (narrative?.parameters?.topic_modeling_dashboard_dates_preset) {
      let selected = dateRangePresetOptions.filter(
        (option) =>
          option.value ===
          narrative?.parameters?.topic_modeling_dashboard_dates_preset,
      );

      setDateRangePreset(selected[0]);
      setAddDateRangeSettings(true);
    }

    if (narrative?.parameters?.topic_modeling_dashboard_start_date) {
      setStartDatePreset(
        new Date(narrative?.parameters?.topic_modeling_dashboard_start_date),
      );
    }

    if (narrative?.parameters?.topic_modeling_dashboard_end_date) {
      setEndDatePreset(
        new Date(narrative?.parameters?.topic_modeling_dashboard_end_date),
      );
    }

    if (narrative?.parameters?.topic_modeling_dashboard_sort) {
      let defaultSorting = narrative?.parameters?.topic_modeling_dashboard_sort;
      setAddDefaultSorting(true);
      setDefaultSorting({
        fieldName: defaultSorting.replace('-', ''),
        isAscending: !defaultSorting.startsWith('-'),
      });
    }
    if (narrative?.parameters?.topic_modeling_translate_language) {
      setTopicModelingTranslationLanguage({
        value: narrative?.parameters?.topic_modeling_translate_language,
        label: topicModelingTranslationLanguageOptions.find(
          (option) =>
            option.value ===
            narrative?.parameters?.topic_modeling_translate_language,
        )?.label,
      });
    }

    if (narrative?.parameters?.exclusive_filters) {
      setLanguagesExclude(
        narrative?.parameters?.exclusive_filters.includes('languages'),
      );
      setCountriesExclude(
        narrative?.parameters?.exclusive_filters.includes(
          'source_origin_country_ids',
        ),
      );
      setSourceGroupsExclude(
        narrative?.parameters?.exclusive_filters.includes('source_group_ids'),
      );
      setSourcesExclude(
        narrative?.parameters?.exclusive_filters.includes('source_ids'),
      );
      setAffiliationCountriesExclude(
        narrative?.parameters?.exclusive_filters.includes(
          'source_state_affiliated_country_ids',
        ),
      );
      setFeedsExclude(
        narrative?.parameters?.exclusive_filters.includes('feed_ids'),
      );
      setGeoGroupExclude(
        narrative?.parameters?.exclusive_filters.includes('country_group_ids'),
      );
      setShowBotsOnly(
        !narrative?.parameters?.exclusive_filters.includes('source_bots'),
      );
    }

    if (narrative?.parameters?.topic_modeling_widget_config) {
      const config = narrative.parameters?.topic_modeling_widget_config;
      setWidgetsSettings(true);
      setSummaryPanel(
        summaryPanelOptions.find(
          (option) => option.value === config.summary_panel,
        ),
      );
      setEventCard(
        eventCardOptions.find((option) => option.value === config.event_card),
      );
      setOpinionCard(
        opinionCardOptions.find(
          (option) => option.value === config.opinion_card,
        ),
      );
      setSentimentBar(
        sentimentBarOptions.filter((option) =>
          config.sentiment_bar?.includes(option.value),
        ),
      );
      setGeoWidget(config.geo_widget);
      setTopActors(
        topActorsOptions.find((option) => option.value === config.top_actors),
      );
      setActorTypes(config.actor_types);
      setEvolutionTabs(
        evolutionTabsOptions.filter((option) =>
          config?.evolution_tabs?.includes(option?.value),
        ),
      );
      setEvolutionBreakdowns(
        evolutionBreakdownsOptions.filter((option) =>
          config?.evolution_breakdowns?.includes(option?.value),
        ),
      );
      setEvolutionChart(config.evolution_chart);
    }

    setIsManuallyManaged(narrative?.is_manually_managed);

    setStoriesVectorsModel(
      storiesVectorsModelOptions.find(
        (option) =>
          option.value === narrative?.parameters?.stories_vectors_model,
      ),
    );
    setIncludeCommentsToStories(
      narrative?.parameters?.include_comments_to_stories,
    );
  }, [narrative]);

  const container = ({ children }) => {
    return (
      <div className="react-datepicker custom-datepicker">
        <CalendarContainer className={styles.container}>
          <div style={{ position: 'relative' }}>{children}</div>
        </CalendarContainer>
      </div>
    );
  };

  let topicModelingEntityOptions = [
    { label: t('Events only'), value: 'EVENTS_AND_OPINIONS_V2' },
  ];

  const sentimentAnalysisTitle = {
    SENTIMENT_STATEMENT: {
      title: t('Topic/Narrative'),
      hint: t(
        'Give a short name of a topic, narrative or opinion to measure reaction to. E.g.: ceasefire in Gaza, Covid vaccines can cause death, sending Western troops to Ukraine.',
      ),
    },
    SENTIMENT_PERSON: {
      title: t('Person'),
      hint: t(
        'Write a name and minimum necessary content that helps identify a person in content. E.g.: Vladimir Putin, leader of PWC Wagner',
      ),
    },
    SENTIMENT_ORGANIZATION: {
      title: t('Organization'),
      hint: t('Write a name of an organization. E.g.: UN, EU'),
    },
    SENTIMENT_COUNTRY: {
      title: t('Country'),
      hint: t('Write a name of a country. E.g.: Russia, USA'),
    },
    SENTIMENT_CUSTOM: {
      title: t('Custom sentiment option'),
      hint: t(
        'Write a custom keyword or phrase to measure sentiment towards. E.g.: "I love my country"',
      ),
    },
  };

  const dateRangePresetOptions = [
    { value: 'FIXED', label: t('Fixed date range') },
    { value: 'YESTERDAY', label: t('Yesterday') },
    { value: 'YESTERDAY_TODAY', label: t('Yesterday - Today') },
    { value: 'LAST_7_DAYS', label: t('Last 7 days') },
    { value: 'CURRENT_WEEK', label: t('Current week') },
    { value: 'LAST_WEEK', label: t('Last week') },
  ];

  const handleSelectAllAffiliations = () => {
    setAffliationAll(!affiliationAll);
    if (!affiliationAll === true) {
      setAffiliationCountries([{ value: 'all', label: t('Select all') }]);
    } else {
      setAffiliationCountries([]);
    }
  };

  const getQuotaModalComponent = () => {
    if (!quotaModal?.isOpen || !currentQuota?.billing_mode) return null;

    const commonProps = {
      isOpen: quotaModal.isOpen,
      isTopicModeling:
        narrativeTypes[currentNarrativeType]?.type === 'TOPIC_MODELING' &&
        limitTMUsage,
      onClose: () => {
        setQuotaModal({ isOpen: false, data: '' });
        setIsQuotaChecked(false);
      },
      data: quotaModal.data,
      buttonText: t('Save case'),
      onChange: (e) => {
        setQuotaModal({ isOpen: false, data: '' });
        handleSubmit(e);
      },
    };

    switch (currentQuota.billing_mode) {
      case 'v0.2':
        return <QuotaCheckModal {...commonProps} />;
      case 'v0.1':
        return <QuotaCheckModalOld {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.root} translate="no">
      <div className="page-header">
        <div className="breadcrumb">
          <span>
            <Link to="/narratives">{t('Cases')}</Link>
          </span>
          <span>
            <Link to={`/narratives/${narrativeId}`}>{values.name}</Link>
          </span>
          <span>{t('Edit')}</span>
        </div>
      </div>

      <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
        <form method="post" onSubmit={handleSubmit}>
          <div className="form-wrapper">
            <div className="form-wrapper-header">
              <h2>{t('Edit case')}</h2>
              <div className="form-wrapper-header-actions">
                {currentUser.type !== 'UNDERPRIVILEGED' ? (
                  <div className="form-element">
                    <Switch
                      id="case-active"
                      value={isActive}
                      onChange={setIsActive}
                    />
                    <label className="active-case-label" htmlFor="case-active">
                      {t('Active')}
                    </label>
                  </div>
                ) : (
                  ''
                )}
              </div>
            </div>

            <div className="group-card">
              <div className="group-card-row">
                <div
                  className={
                    errors.name ? 'form-element error' : 'form-element'
                  }
                >
                  <label htmlFor="name">{t('Name')}&#42;</label>
                  <input
                    className=""
                    id="name"
                    name="name"
                    type="text"
                    onChange={handleChange}
                    required
                  />
                  <div className="error-message">{errors.name}</div>
                </div>

                <div
                  className={
                    errors.description ? 'form-element error' : 'form-element'
                  }
                >
                  <label>{t('Description')}</label>
                  <textarea
                    className=""
                    id="description"
                    name="description"
                    onChange={handleChange}
                    type="text"
                  ></textarea>
                  <div className="error-message">{errors.description}</div>
                </div>
              </div>
              {isCaseModerationEnabled &&
              currentUser.type !== 'UNDERPRIVILEGED' ? (
                <div
                  className={
                    errors.reviewStatus ? 'form-element error' : 'form-element'
                  }
                >
                  <label>{t('Status')}</label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={false}
                    placeholder={t('Status')}
                    options={reviewStatusOptions}
                    name="reviewStatus"
                    value={reviewStatus}
                    onChange={(value) => setReviewStatus(value)}
                  />
                </div>
              ) : (
                ''
              )}
              {narrative ? (
                <NarrativeTypeChooser
                  currentNarrativeTypeIndex={currentNarrativeType}
                  onChange={(selected) => setCurrentNarrativeType(selected)}
                />
              ) : (
                <div className="widgetLoader">
                  {' '}
                  <LoaderSmall />
                </div>
              )}
            </div>
          </div>

          {['NARRATIVE', 'TOPIC_MODELING'].includes(
            narrativeTypes[currentNarrativeType]?.type,
          ) ? (
            <div className="form-wrapper">
              <h2>{t('Search')}</h2>
              <div className="group-card">
                <div className="form-element">
                  <label className="label">{t('Keywords')}</label>
                  <QueryInput
                    onMixedNearPairChange={setHasMixedNearPair}
                    isAdvanced={isAdvancedSyntax}
                    onChange={onKeywordsChange}
                    isInvalid={!isValidQuery}
                    disabled={disableFilters}
                    keywords={keywords}
                  />
                </div>

                <div className="group-card-row">
                  <div
                    className={
                      errors.startDate || errors.endDate
                        ? 'form-element error'
                        : 'form-element'
                    }
                  >
                    <label>
                      {isCaseOpen ? t('Start date') : t('Date range')}
                    </label>
                    <div className="form-date-picker-wrapper">
                      <DatePickerWrapper
                        onChange={(startDate, endDate) => {
                          setStartDate(startDate);
                          setEndDate(endDate);
                        }}
                        initStartDate={startDate}
                        initEndDate={endDate}
                        isRange={isCaseOpen ? false : true}
                      />

                      <span className="open-case-settings form-checkbox-group">
                        <span
                          className="form-checkbox"
                          data-tooltip-id="open-case-tooltip"
                          data-tooltip-content={t(
                            'Continuously fetches new results without end date',
                          )}
                        >
                          <input
                            type="checkbox"
                            id="open-case"
                            checked={isCaseOpen}
                            onChange={(e) => setIsCaseOpen(e.target.checked)}
                          />
                          <label className="label" htmlFor="open-case">
                            {t('Ongoing Case')}
                          </label>
                        </span>
                        <Tooltip id="open-case-tooltip" place="top" />
                      </span>
                    </div>

                    <div className="error-message">
                      {errors.startDate || errors.endDate}
                    </div>
                  </div>

                  <div className="form-element">
                    <label htmlFor="case-multilang" className="label">
                      {t('Multilingual search')}

                      <div className="sub-label">
                        {t('Search for relevant messages in other languages')}
                      </div>
                    </label>
                    <Switch
                      id="case-multilang"
                      value={isMultilang}
                      onChange={setIsMultilang}
                      isDisabled={isParametersDisabled}
                    />

                    {isMultilang ? (
                      <div
                        className={
                          errors.language
                            ? 'form-element error'
                            : 'form-element'
                        }
                      >
                        <label>{t('Select native language')}</label>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isDisabled={isParametersDisabled || !isMultilang}
                          isMulti={false}
                          placeholder={t('Language')}
                          options={keywordsNativeLanguageOptions.map(
                            (option) => {
                              return {
                                value: option.value,
                                label: t(option.label),
                              };
                            },
                          )}
                          name="language"
                          value={nativeLang}
                          // menuIsOpen={true}
                          onChange={(values) => setNativeLang(values)}
                        />
                        <div className="error-message">{errors.language}</div>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>

                {hasMixedNearPair ? (
                  <div className="form-element">
                    <NearMixedPairWarning startDate={startDate} isAdvanced={isAdvancedSyntax} />
                  </div>
                ) : null}
              </div>

              <div className="form-group-header">
                <span>{t('Platforms and Content')}</span>
              </div>

              <div className="group-card">
                <>
                  <PlatformsFilter
                    selected={sourceTypes}
                    onChange={setSourceTypes}
                    error={errors.sourceTypes}
                    isDisabled={isParametersDisabled}
                  />

                  <ContentTypesFilter
                    selected={contentTypes}
                    onChange={(selected, collectComments) => {
                      setContentTypes(selected);
                      if (collectComments !== collectCommentsToPosts) {
                        setCollectCommentsToPosts(collectComments);
                      }
                    }}
                    selectedPlatforms={sourceTypes}
                    collectComments={collectCommentsToPosts}
                    error={errors.contentTypes}
                    isDisabled={isParametersDisabled}
                  />
                </>
                <div
                  className={
                    errors.languages ? 'form-element error' : 'form-element'
                  }
                >
                  <div className={styles.labelContainer}>
                    <label className={styles.label}>
                      {t('Content languages')}
                    </label>
                    <ExcludeSwitch
                      id="language-switch"
                      value={languagesExclude}
                      onChange={setLanguagesExclude}
                      color="red"
                      direction={'row-reverse'}
                      isDisabled={isParametersDisabled}
                    />
                  </div>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={true}
                    placeholder={t('Select languages')}
                    options={languageOptions.map((option) => {
                      return { value: option.value, label: t(option.label) };
                    })}
                    name="languages"
                    value={languages}
                    onChange={(values) => setLanguages(values)}
                    isDisabled={isParametersDisabled}
                  />
                  <div className="error-message">{errors.languages}</div>
                </div>
              </div>

              <div className="form-group-header">
                <span>{t('Actors and Behavior')}</span>
              </div>

              <div className="group-card">
                {showCountryGroupsFilter ? (
                  <div
                    className={
                      errors.sources ? 'form-element error' : 'form-element'
                    }
                  >
                    <div className={styles.labelContainer}>
                      <label className={styles.label}>
                        {t('Actor origin country groups')}
                      </label>
                      <ExcludeSwitch
                        id="geoGroup-switch"
                        value={geoGroupExclude}
                        onChange={setGeoGroupExclude}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={isParametersDisabled}
                      />
                    </div>
                    <GeoGroupFilter
                      selected={geoGroup}
                      disabled={false}
                      onChange={(checklist) => setGeoGroup(checklist)}
                      style={{ fontSize: '16px' }}
                      isDisabled={isParametersDisabled}
                    />
                    <div className="error-message">{errors.geoGroup}</div>
                  </div>
                ) : (
                  ''
                )}

                <div
                  className={
                    errors.sources ? 'form-element error' : 'form-element'
                  }
                >
                  <div className={styles.labelContainer}>
                    <label className={styles.label}>
                      {t('Actor origin countries')}
                    </label>
                    <ExcludeSwitch
                      id="countries-switch"
                      value={countriesExclude}
                      onChange={setCountriesExclude}
                      color="red"
                      direction={'row-reverse'}
                      isDisabled={isParametersDisabled}
                    />
                  </div>
                  <Async
                    className="react-select-container"
                    classNamePrefix="react-select"
                    cacheOptions
                    defaultOptions={countriesTop}
                    isMulti={true}
                    loadOptions={countriesPromiseOptions}
                    placeholder={t('Search or select countries')}
                    noOptionsMessage={() => t('Please enter at least 1 symbol')}
                    loadingMessage={() => t('Loading...')}
                    name="countries"
                    value={countries}
                    onChange={(values) => setCountries(values)}
                    isDisabled={isParametersDisabled}
                  />
                  <div className="error-message">{errors.countries}</div>
                </div>
                <div
                  className={
                    errors.sourceGroups ? 'form-element error' : 'form-element'
                  }
                  data-tooltip-id="actor-groups-tooltip"
                  data-tooltip-content={getDisabledTooltip('Actor groups')}
                >
                  <div className={styles.labelContainer}>
                    <label className={styles.label}>{t('Actor groups')}</label>
                    <ExcludeSwitch
                      id="actor-groups-switch"
                      value={sourceGroupsExclude}
                      onChange={setSourceGroupsExclude}
                      color="red"
                      direction={'row-reverse'}
                      isDisabled={
                        isFilterDisabled('Actor groups') || isParametersDisabled
                      }
                    />
                  </div>
                  <Async
                    className="react-select-container"
                    classNamePrefix="react-select"
                    cacheOptions
                    defaultOptions
                    isMulti={true}
                    loadOptions={promiseOptions}
                    placeholder={t('Search or select actor groups')}
                    name="narratives"
                    value={sourceGroups}
                    onChange={(values) =>
                      isFilterDisabled('Actor groups')
                        ? null
                        : setSourceGroups(values)
                    }
                    isDisabled={
                      isFilterDisabled('Actor groups') || isParametersDisabled
                    }
                    isSearchable={!isFilterDisabled('Actor groups')}
                    menuIsOpen={
                      isFilterDisabled('Actor groups') ? false : undefined
                    }
                  />
                  <div className="error-message">{errors.sourceGroups}</div>
                </div>
                <div
                  className={
                    errors.sources ? 'form-element error' : 'form-element'
                  }
                >
                  <div className={styles.labelContainer}>
                    <label className={styles.label}>
                      {t('Individual actors')}
                    </label>
                    <ExcludeSwitch
                      id="actor-switch"
                      value={sourcesExclude}
                      onChange={setSourcesExclude}
                      color="red"
                      direction={'row-reverse'}
                      isDisabled={isParametersDisabled}
                    />
                  </div>
                  <Async
                    className="react-select-container"
                    classNamePrefix="react-select"
                    cacheOptions
                    defaultOptions
                    isMulti={true}
                    loadOptions={sourcesPromiseOptions}
                    components={{ Option: IconOption }}
                    placeholder={t('Search or select actors')}
                    noOptionsMessage={() => t('Please enter at least 1 symbol')}
                    loadingMessage={() => t('Loading...')}
                    name="sources"
                    value={sources}
                    onChange={(values) => setSources(values)}
                    isClearable={true}
                    isDisabled={isParametersDisabled}
                  />
                  <div className="error-message">{errors.sources}</div>
                </div>
              </div>

              <div className="form-group-header">{t('Actor behavior')}</div>
              <div className="group-card">
                {showFeedsFilter ? (
                  <div
                    className={
                      errors.feeds ? 'form-element error' : 'form-element'
                    }
                  >
                    <div className={styles.labelContainer}>
                      <label className={styles.label}>{t('Feeds')}</label>
                      <ExcludeSwitch
                        id="feeds-switch"
                        value={feedsExclude}
                        onChange={setFeedsExclude}
                        color="red"
                        direction={'row-reverse'}
                        isDisabled={isParametersDisabled}
                      />
                    </div>
                    <Async
                      className="react-select-container"
                      classNamePrefix="react-select"
                      cacheOptions
                      defaultOptions
                      isMulti={true}
                      loadOptions={promiseFeedOptions}
                      placeholder={t('Search or select feeds')}
                      name="narratives"
                      value={feeds}
                      onChange={(values) => setFeeds(values)}
                      isDisabled={isParametersDisabled}
                    />
                    <div className="error-message">{errors.feeds}</div>
                  </div>
                ) : (
                  ''
                )}

                <div
                  className={
                    errors.affiliationCountries
                      ? 'form-element error'
                      : 'form-element'
                  }
                  data-tooltip-id="state-affiliated-tooltip"
                  data-tooltip-content={getDisabledTooltip('State-affiliated')}
                >
                  <div className={styles.labelContainer}>
                    <label className={styles.label}>
                      {t('State-affiliated actors')}
                    </label>
                    <ExcludeSwitch
                      id="affiliation-country-switch"
                      value={affiliationCountriesExclude}
                      onChange={setAffiliationCountriesExclude}
                      color="red"
                      direction={'row-reverse'}
                      isDisabled={
                        isFilterDisabled('State-affiliated') ||
                        isParametersDisabled
                      }
                    />
                  </div>
                  <Async
                    className="react-select-container"
                    classNamePrefix="react-select"
                    cacheOptions
                    defaultOptions={[
                      {
                        value: null,
                        label: t('Select all affiliation countries'),
                      },
                      ...countriesTop,
                    ]}
                    isMulti={true}
                    loadOptions={countriesPromiseOptions}
                    placeholder={t('Search or select countries')}
                    noOptionsMessage={() => t('Please enter at least 1 symbol')}
                    loadingMessage={() => t('Loading...')}
                    name="affiliationCountries"
                    value={affiliationCountries}
                    onChange={(values) => {
                      if (
                        isFilterDisabled('State-affiliated') ||
                        isParametersDisabled
                      ) {
                        return;
                      } else if (values?.find((v) => v.value === null)) {
                        setAffliationAll(true);
                        setAffiliationCountries([
                          {
                            value: null,
                            label: t('Select all affiliation countries'),
                          },
                        ]);
                      } else {
                        setAffliationAll(false);
                        setAffiliationCountries(values);
                      }
                    }}
                    isDisabled={
                      isFilterDisabled('State-affiliated') ||
                      isParametersDisabled
                    }
                    isSearchable={
                      !(
                        affiliationAll || isFilterDisabled('State-affiliated')
                      ) && !isParametersDisabled
                    }
                    menuIsOpen={
                      isFilterDisabled('State-affiliated') ||
                      isParametersDisabled
                        ? false
                        : undefined
                    }
                    isOptionDisabled={() => affiliationAll}
                  />
                  <div className="error-message">
                    {errors.affiliationCountries}
                  </div>
                </div>

                {hideRiskActors ? (
                  ''
                ) : (
                  <div
                    className={
                      errors.discreditations
                        ? 'form-element error'
                        : 'form-element'
                    }
                    data-tooltip-id="high-risk-accounts-tooltip"
                    data-tooltip-content={getDisabledTooltip(
                      'High risk accounts',
                    )}
                  >
                    <label>{t('High risk accounts')}</label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isMulti={true}
                      placeholder={t('Select one or more risk types')}
                      options={IoCTypeOptionsList(t)}
                      name="discreditations"
                      value={discreditations}
                      onChange={(values) =>
                        isFilterDisabled('High risk accounts') ||
                        isParametersDisabled
                          ? null
                          : setDiscreditations(values)
                      }
                      isDisabled={
                        isFilterDisabled('High risk accounts') ||
                        isParametersDisabled
                      }
                      isSearchable={
                        !isFilterDisabled('High risk accounts') &&
                        !isParametersDisabled
                      }
                      menuIsOpen={
                        isFilterDisabled('High risk accounts') ||
                        isParametersDisabled
                          ? false
                          : undefined
                      }
                    />
                    <div className="error-message">
                      {errors.discreditations}
                    </div>
                  </div>
                )}

                <div className="form-element">
                  <label htmlFor="all_bots" className={styles.label}>
                    {t('Inauthentic behavior')}
                  </label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={botsOptions.map((option) => ({
                      ...option,
                      isDisabled:
                        option.value === true &&
                        isFilterDisabled('Show only inauthentic behavior'),
                    }))}
                    value={botsOptions.find((o) => o.value === showBotsOnly)}
                    onChange={(v) => {
                      // Prevent selecting "Show only" if other exclusive filters are active
                      if (
                        v.value === true &&
                        isFilterDisabled('Show only inauthentic behavior')
                      ) {
                        return;
                      }
                      setShowBotsOnly(v.value);
                    }}
                    placeholder={t('Select inauthentic behavior')}
                    isClearable={false}
                    isDisabled={isParametersDisabled}
                  />
                </div>
              </div>
            </div>
          ) : (
            ''
          )}

          {showAIAnalysisSection ? (
            <div className="form-wrapper">
              <div className="form-wrapper-header with-margin">
                <h2>{t('AI Analysis')}</h2>
              </div>

              {aspectBasedSentimentAvailable ? (
                <>
                  <div className="form-group-header">
                    <span>{t('Sentiment analysis')}</span>
                  </div>
                  <div className="group-card">
                    <div className="group-card-row">
                      <div className="form-element">
                        <label
                          htmlFor="is-aspect-based-sentiment-enabled"
                          className="label"
                        >
                          <div>
                            <span className="label-icon">
                              <AIIcon />
                            </span>
                            {t('Contextual sentiment')}
                          </div>

                          <div className="sub-label">
                            {t(
                              'Detect contextual sentiments toward specific people, countries, events and more.',
                            )}
                          </div>
                        </label>
                        <Switch
                          id="is-aspect-based-sentiment-enabled"
                          value={isAspectBasedSentimentEnabled}
                          onChange={setIsAspectBasedSentimentEnabled}
                          isDisabled={isParametersDisabled}
                        />
                      </div>

                      {isAspectBasedSentimentEnabled ? (
                        <div>
                          <div
                            className={
                              errors.aspectBasedSentimentObject
                                ? 'form-element error'
                                : 'form-element'
                            }
                          >
                            <label htmlFor="aspect-based-sentiment-object">
                              {t('Sentiment analysis')}
                            </label>
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              placeholder={t(
                                'Choose context for sentiment analysis',
                              )}
                              options={aspectBasedSentimentOptions}
                              id="aspect-based-sentiment-object"
                              name="aspect-based-sentiment-object"
                              value={aspectBasedSentimentObject}
                              onChange={(value) =>
                                setAspectBasedSentimentObject(value)
                              }
                              menuPlacement="auto"
                              isDisabled={isParametersDisabled}
                            />
                            <div className="error-message">
                              {errors.aspectBasedSentimentObject}
                            </div>
                          </div>
                          {aspectBasedSentimentObject ? (
                            <div
                              className={
                                errors.aspectBasedSentimentSubject
                                  ? 'form-element error'
                                  : 'form-element'
                              }
                            >
                              <label htmlFor="aspect-based-sentiment-subject">
                                {
                                  sentimentAnalysisTitle[
                                    aspectBasedSentimentObject?.value
                                  ]?.title
                                }
                                <span className="sub-label">
                                  {' '}
                                  {
                                    sentimentAnalysisTitle[
                                      aspectBasedSentimentObject?.value
                                    ]?.hint
                                  }
                                </span>
                              </label>
                              <input
                                className=""
                                id="aspect-based-sentiment-subject"
                                name="aspect-based-sentiment-subject"
                                type="text"
                                placeholder={
                                  sentimentAnalysisTitle[
                                    aspectBasedSentimentObject?.value
                                  ]?.placeholder
                                }
                                onChange={(e) =>
                                  setAspectBasedSentimentSubject(e.target.value)
                                }
                                required={isAspectBasedSentimentEnabled}
                                value={aspectBasedSentimentSubject}
                                isDisabled={isParametersDisabled}
                              />
                              <div className="error-message">
                                {errors.aspectBasedSentimentSubject}
                              </div>
                            </div>
                          ) : (
                            ''
                          )}
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </>
              ) : (
                ''
              )}

              {showTopicsSection ? (
                <div>
                  {narrativeTypes[currentNarrativeType]?.type !==
                    'TOPIC_MODELING' && showStories ? (
                    <div className="form-group-header">
                      <span>{t('Publication grouping')}</span>
                    </div>
                  ) : (
                    ''
                  )}

                  <div className="group-card">
                    {narrativeTypes[currentNarrativeType]?.type !==
                      'TOPIC_MODELING' && showStories ? (
                      <div className="group-card-row">
                        <div className="form-element">
                          <label htmlFor="create-stories" className="label">
                            <div>
                              <span className="label-icon">
                                <AIIcon />
                              </span>
                              {t('Stories')}
                            </div>
                            <div className="sub-label">
                              {t(
                                'Group publications into key topics based on semantic similarity.',
                              )}
                            </div>
                          </label>
                          <Switch
                            id="create-stories"
                            value={createStories}
                            onChange={setCreateStories}
                            isDisabled={isParametersDisabled}
                          />
                        </div>
                        {showStoriesParameters && createStories ? (
                          <div className="form-element-group">
                            <div className="form-element">
                              <div className="form-checkbox-group">
                                <div className="form-checkbox">
                                  <input
                                    type="checkbox"
                                    id="include-comments-to-stories"
                                    checked={
                                      createStories && includeCommentsToStories
                                    }
                                    onChange={(e) =>
                                      setIncludeCommentsToStories(
                                        e.target.checked,
                                      )
                                    }
                                    disabled={
                                      !createStories || isParametersDisabled
                                    }
                                  />

                                  <label
                                    htmlFor="include-comments-to-stories"
                                    className="label"
                                  >
                                    {t('Include comments to stories')}
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div className="form-element">
                              <label htmlFor="stories-vectors-model">
                                {t('Vectorization model to use')}
                              </label>
                              <Select
                                className="select-filter"
                                classNamePrefix="select-filter"
                                placeholder={t(
                                  'Choose vectorization model for stories',
                                )}
                                options={storiesVectorsModelOptions}
                                id="stories-vectors-model"
                                name="stories-vectors-model"
                                value={
                                  createStories && storiesVectorsModel
                                    ? storiesVectorsModel
                                    : storiesVectorsModelOptions[0]
                                }
                                onChange={setStoriesVectorsModel}
                                menuPlacement="auto"
                                isDisabled={
                                  !createStories || isParametersDisabled
                                }
                              />
                            </div>

                            <div
                              className={
                                errors.storiesCandidatesMinThreshold
                                  ? 'form-element error'
                                  : 'form-element'
                              }
                            >
                              <label htmlFor="stories_candidates_min_threshold">
                                {t('Candidates min threshold')}
                              </label>
                              <RangeSlider
                                min={0.05}
                                max={1}
                                step={0.05}
                                data={storiesCandidatesMinThreshold}
                                width="100%"
                                onChange={(value) =>
                                  setStoriesCandidatesMinThreshold(value)
                                }
                                disabled={
                                  !createStories || isParametersDisabled
                                }
                              />
                              <div className="error-message">
                                {errors.storiesCandidatesMinThreshold}
                              </div>
                            </div>

                            <div className="form-element">
                              <label
                                htmlFor="use-max-grouping-distance"
                                className={styles.label}
                              >
                                {t('Use max grouping distance')}
                              </label>
                              <Switch
                                id="use-max-grouping-distance"
                                value={
                                  createStories && useStoriesGroupingMaxDistance
                                }
                                onChange={setUseStoriesGroupingMaxDistance}
                                isDisabled={
                                  !createStories || isParametersDisabled
                                }
                              />
                            </div>

                            {useStoriesGroupingMaxDistance ? (
                              <div
                                className={
                                  errors.storiesGroupingMaxDistance
                                    ? 'form-element error'
                                    : 'form-element'
                                }
                              >
                                <label htmlFor="stories_grouping_max_distance">
                                  {t('Grouping max distance')}
                                </label>
                                <RangeSlider
                                  min={0.05}
                                  max={1}
                                  step={0.05}
                                  data={storiesGroupingMaxDistance}
                                  width="100%"
                                  onChange={(value) =>
                                    setStoriesGroupingMaxDistance(value)
                                  }
                                  disabled={
                                    !useStoriesGroupingMaxDistance ||
                                    !createStories ||
                                    isParametersDisabled
                                  }
                                />
                                <div className="error-message">
                                  {errors.storiesGroupingMaxDistance}
                                </div>
                              </div>
                            ) : (
                              ''
                            )}
                          </div>
                        ) : (
                          ''
                        )}{' '}
                      </div>
                    ) : (
                      ''
                    )}

                    {narrativeTypes[currentNarrativeType]?.type ===
                    'TOPIC_MODELING' ? (
                      <>
                        <div className="group-card-header">
                          {t('Topic modeling')}
                          <div className="group-card-secondary-text">
                            <p>
                              {t(
                                'Transform information space into coherent topics',
                              )}
                            </p>
                          </div>
                        </div>
                        <div
                          className={
                            errors.topicModelingEntity
                              ? 'form-element error'
                              : 'form-element'
                          }
                        >
                          <label htmlFor="topic-modeling-entity">
                            {t('Topic modeling entity')}&#42;
                          </label>
                          <Select
                            className="select-filter"
                            classNamePrefix="select-filter"
                            placeholder={t(
                              'Choose entity for topic modeling analysis',
                            )}
                            options={topicModelingEntityOptions}
                            id="topic-modeling-entity"
                            name="topic-modeling-entity"
                            value={topicModelingEntity}
                            onChange={(value) => setTopicModelingEntity(value)}
                            menuPlacement="auto"
                            required={true}
                            isDisabled={isParametersDisabled}
                          />
                          <div className="error-message">
                            {errors.topicModelingEntity}
                          </div>
                        </div>
                        {topicModelingEntity ? (
                          <div
                            className={
                              errors.TMlanguages
                                ? 'form-element error'
                                : 'form-element'
                            }
                          >
                            <label htmlFor="topic-modeling-entity">
                              {t('Translate topics into')}
                            </label>
                            <Select
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isMulti={false}
                              placeholder={t('Choose language')}
                              options={topicModelingTranslationLanguageOptions}
                              name="TMlanguages"
                              value={topicModelingTranslationLanguage}
                              onChange={(value) =>
                                setTopicModelingTranslationLanguage(value)
                              }
                              isDisabled={
                                (topicModelingEntity?.value !== 'EVENTS_ONLY' &&
                                  topicModelingEntity?.value !==
                                    'EVENTS_AND_OPINIONS_V2') ||
                                isParametersDisabled
                              }
                            />
                            <div className="error-message">
                              {errors.TMlanguages}
                            </div>
                          </div>
                        ) : (
                          ''
                        )}
                      </>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              ) : (
                ''
              )}
            </div>
          ) : (
            ''
          )}

          <div className="form-wrapper">
            {narrativeTypes[currentNarrativeType]?.type === 'TOPIC_MODELING' ? (
              <h2>{t('Topic modeling additional settings')}</h2>
            ) : (
              <h2>{t('Case additional settings')}</h2>
            )}
            <div className="group-card">
              {currentUser?.is_super_admin ? (
                <div className="form-element">
                  <label className={styles.label} htmlFor="manual-managed">
                    {t('Manually managed')}
                  </label>
                  <Switch
                    id="manual-managed"
                    value={isManuallyManaged}
                    onChange={setIsManuallyManaged}
                  />
                </div>
              ) : (
                ''
              )}
            </div>

            {!['TOPIC_MODELING'].includes(
              narrativeTypes[currentNarrativeType]?.type,
            ) ? (
              <div className="group-card">
                <div className="form-element">
                  <label htmlFor="case-refresh" className={styles.label}>
                    {t('Refresh whole case history')}
                  </label>
                  <Switch
                    id="case-refresh"
                    value={refreshHistory}
                    onChange={(value) => setrefreshHistory(value)}
                    isDisabled={isParametersDisabled}
                  />
                  <span className="switchHint">
                    {refreshHistory
                      ? t('Rebuild case from scratch using new settings.')
                      : t(
                          'Keep current content; apply settings to new publications only.',
                        )}
                  </span>
                </div>
              </div>
            ) : (
              ''
            )}

            {['TOPIC_MODELING'].includes(
              narrativeTypes[currentNarrativeType]?.type,
            ) && currentUser?.is_super_admin ? (
              <div className="group-card">
                <div className={'form-element'}>
                  <label>{t('Organizations')}</label>
                  <Async
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isMulti={true}
                    loadOptions={workspacePromiseOptions}
                    placeholder={t('Organizations')}
                    name="threatOrganizations"
                    value={workspaces}
                    onChange={(values) => setWorkspaces(values)}
                  />
                </div>
              </div>
            ) : (
              ''
            )}

            {['TOPIC_MODELING'].includes(
              narrativeTypes[currentNarrativeType]?.type,
            ) && topicModelingEntity?.value !== 'OPINIONS_ONLY' ? (
              <>
                <div className="group-card">
                  <div className="group-card-row">
                    <div className="form-element">
                      <label htmlFor="dateRange" className={styles.label}>
                        {t('Date range filter preset')}
                        <div className="sub-label">
                          {t(
                            'Define the date range that will be selected by default for topic modeling.',
                          )}
                        </div>
                      </label>
                      <Switch
                        id="dateRange"
                        value={addDateRangeSettings}
                        onChange={(v) => {
                          setAddDateRangeSettings(v);
                        }}
                      />
                    </div>
                    {addDateRangeSettings ? (
                      <div className="form-element-group">
                        <div
                          className={
                            errors.dateRangePreset
                              ? 'form-element error'
                              : 'form-element'
                          }
                        >
                          <label>{t('Date range filter preset')}</label>
                          <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder={t('Select option')}
                            options={dateRangePresetOptions}
                            name="dateRangePreset"
                            value={dateRangePreset}
                            onChange={(values) => setDateRangePreset(values)}
                          />

                          <div className="error-message">
                            {errors.dateRangePreset}
                          </div>
                        </div>
                        {dateRangePreset &&
                        dateRangePreset.value === 'FIXED' ? (
                          <div className="group-card-row">
                            <div
                              className={
                                errors.startDatePreset
                                  ? 'form-element error'
                                  : 'form-element'
                              }
                            >
                              <label>{t('Start date')}</label>
                              <DatePicker
                                dateFormat="yyyy-MM-dd"
                                selected={startDatePreset}
                                onChange={(date) => setStartDatePreset(date)}
                                calendarContainer={container}
                                minDate={startDate}
                                maxDate={endDate || new Date()}
                                required
                                showDisabledMonthNavigation
                                placeholderText={t('Select date')}
                              />
                              <div className="error-message">
                                {errors.startDatePreset}
                              </div>
                            </div>

                            <div
                              className={
                                errors.endDatePreset
                                  ? 'form-element error'
                                  : 'form-element'
                              }
                            >
                              <label>{t('End date')}</label>
                              <DatePicker
                                dateFormat="yyyy-MM-dd"
                                selected={endDatePreset}
                                onChange={(date) => setEndDatePreset(date)}
                                calendarContainer={container}
                                minDate={startDatePreset}
                                maxDate={endDate || new Date()}
                                showDisabledMonthNavigation
                                placeholderText={t('Select date')}
                              />
                              <div className="error-message">
                                {errors.endDatePreset}
                              </div>
                            </div>
                          </div>
                        ) : (
                          ''
                        )}
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>

                <div className="group-card">
                  <div className="group-card-row">
                    <div className="form-element">
                      <label htmlFor="defaultSorting" className={styles.label}>
                        {t('Add default sorting')}
                        <div className="sub-label">
                          {t(
                            'Define the default sorting applied to topic modeling results.',
                          )}
                        </div>
                      </label>
                      <Switch
                        id="defaltSorting"
                        value={addDefaultSorting}
                        onChange={(v) => {
                          setAddDefaultSorting(v);
                        }}
                      />
                    </div>
                    {addDefaultSorting ? (
                      <div className="form-element-group">
                        <div
                          className={
                            errors.defaultSorting
                              ? 'form-element error'
                              : 'form-element'
                          }
                        >
                          <label>{t('Sort by')}</label>
                          <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder={t('Select option')}
                            options={sortingMenu}
                            name="defaultSorting"
                            value={sortingMenu.find(
                              (v) => v.value === defaultSorting.fieldName,
                            )}
                            onChange={(values) =>
                              setDefaultSorting({
                                ...defaultSorting,
                                fieldName: values.value,
                              })
                            }
                          />
                        </div>
                        <div
                          className={
                            errors.defaultSortingDirection
                              ? 'form-element error'
                              : 'form-element'
                          }
                        >
                          <label>{t('Sorting order')}</label>
                          <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder={t('Select option')}
                            options={sortingOrder}
                            name="defaultSortingDirection"
                            value={sortingOrder.find(
                              (v) => v.value === defaultSorting.isAscending,
                            )}
                            onChange={(values) =>
                              setDefaultSorting({
                                ...defaultSorting,
                                isAscending: values.value,
                              })
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </>
            ) : (
              ''
            )}

            <div className="group-card">
              <div className="group-card-row">
                <div className="form-element">
                  <label htmlFor="widgets-settings" className={styles.label}>
                    {t('Widgets settings')}
                    <div className="sub-label">
                      {t('Define the widget configuration for topic modeling.')}
                    </div>
                  </label>
                  <Switch
                    id="widgets-settings"
                    value={widgetsSettings}
                    onChange={setWidgetsSettings}
                  />
                </div>

                {widgetsSettings ? (
                  <div className="form-element-group">
                    <div className="form-element">
                      <label>{t('Summary panel')}</label>
                      <Select
                        className="select-filter"
                        classNamePrefix="select-filter"
                        placeholder={t('Select option')}
                        options={summaryPanelOptions}
                        name="summaryPanel"
                        value={summaryPanel}
                        onChange={(value) => setSummaryPanel(value)}
                      />
                    </div>
                    <div className="form-element">
                      <label>{t('Event card')}</label>
                      <Select
                        className="select-filter"
                        classNamePrefix="select-filter"
                        placeholder={t('Select option')}
                        options={eventCardOptions}
                        name="eventCard"
                        value={eventCard}
                        onChange={(value) => setEventCard(value)}
                      />
                    </div>
                    <div className="form-element">
                      <label>{t('Sentiment bar')}</label>
                      <Select
                        className="select-filter"
                        classNamePrefix="select-filter"
                        placeholder={t('Select options')}
                        options={sentimentBarOptions}
                        isMulti={true}
                        name="sentimentSettings"
                        value={sentimentBar}
                        onChange={(value) => setSentimentBar(value)}
                      />
                    </div>
                    <div className="form-element">
                      <label htmlFor="geo-widget" className={styles.label}>
                        {t('Geo widget')}
                      </label>
                      <Switch
                        id="geo-widget"
                        value={geoWidget}
                        onChange={setGeoWidget}
                      />
                    </div>
                    <div className="form-element">
                      <label>{t('Top actors')}</label>
                      <Select
                        className="select-filter"
                        classNamePrefix="select-filter"
                        placeholder={t('Select option')}
                        options={topActorsOptions}
                        name="topActors"
                        value={topActors}
                        onChange={(value) => setTopActors(value)}
                      />
                    </div>
                    <div className="form-element">
                      <label htmlFor="actor-types" className={styles.label}>
                        {t('Actor types')}
                      </label>
                      <Switch
                        id="actor-types"
                        value={actorTypes}
                        onChange={setActorTypes}
                      />
                    </div>
                    <div className="form-element">
                      <label htmlFor="evolution-chart" className={styles.label}>
                        {t('Evolution chart')}
                      </label>
                      <Switch
                        id="evolution-chart"
                        value={evolutionChart}
                        onChange={setEvolutionChart}
                      />
                    </div>
                    <div className="form-element">
                      <label>{t('Evolution chart tabs')}</label>
                      <Select
                        className="select-filter"
                        classNamePrefix="select-filter"
                        placeholder={t('Select options')}
                        options={evolutionTabsOptions}
                        isMulti={true}
                        name="evolutionTabs"
                        value={evolutionTabs}
                        onChange={(value) => setEvolutionTabs(value)}
                      />
                    </div>
                    <div className="form-element">
                      <label>{t('Evolution chart breakdowns')}</label>
                      <Select
                        className="select-filter"
                        classNamePrefix="select-filter"
                        placeholder={t('Select options')}
                        options={evolutionBreakdownsOptions}
                        name="evolutionBreakdowns"
                        isMulti={true}
                        value={evolutionBreakdowns}
                        onChange={(value) => setEvolutionBreakdowns(value)}
                      />
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>

          <div className="form-element">
            <button
              type="submit"
              className="new-button"
              disabled={
                notEnoughQuota || !isValidQuery || isLoading || isQuotaChecked
              }
              data-tooltip-id="save-case"
              data-tooltip-content={
                !notEnoughQuota ? t('Save case') : t('No quota left')
              }
            >
              {isLoading && !caseCreation
                ? t('Checking quota...')
                : isLoading && caseCreation
                ? t('Saving case...')
                : t('Save')}
            </button>
            <Tooltip id="save-case" place="bottom" />

            {!isValidQuery && (
              <span className={styles.errorMessage}>
                {t('Fix the keywords query')}
              </span>
            )}

            {errors.general ? (
              <div className={styles.errorMessage}>
                {t(errors.general)}
                <span onClick={() => setErrors({ ...errors, general: null })}>
                  {' '}
                  <CloseIcon />{' '}
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
        </form>
        {getQuotaModalComponent()}

        {/* Tooltips for disabled filters */}
        {['Actor groups', 'State-affiliated', 'High risk accounts'].map(
          (filterName) =>
            isFilterDisabled(filterName) && (
              <Tooltip
                key={filterName}
                id={`${filterName.toLowerCase().replace(/\s+/g, '-')}-tooltip`}
                place="top"
                style={{
                  fontSize: '14px',
                  textAlign: 'center',
                  marginTop: '1.5rem',
                }}
              />
            ),
        )}

        {/* Special tooltip for "Show only" option when disabled */}
        {isFilterDisabled('Show only inauthentic behavior') && (
          <Tooltip
            id="show-only-disabled-tooltip"
            place="top"
            style={{
              fontSize: '14px',
              textAlign: 'center',
              marginTop: '1.5rem',
            }}
          />
        )}
      </div>
    </div>
  );
}
